import { useContext, useEffect, useState } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Box, Button, CircularProgress, IconButton, Stack, Tooltip } from "@mui/material";
import { CardSwapStatusCode, cardSwapStatusCodes } from "../enums/CardSwapStatusCode";
import { CreatingNewCardDialog } from "../dialogs/CreatingNewCardDialog";
import { NewCardCreatedDialog } from "../dialogs/NewCardCreatedDialog";
import { AuditDialog } from "../dialogs/AuditDialog";
import { CardCollectNotificationFailedDialog } from "../dialogs/CardCollectNotificationFailedDialog";
import { CardSwapFinalDialog } from "../dialogs/CardSwapFinalDialog";
import { CardSwapToggleDialog } from "../dialogs/CardSwapToggleDialog";
import { bhpButtonSx } from "../../../styles/bhpStyles";
import GetAppIcon from '@mui/icons-material/GetApp';
import { EnterNewCardNumber } from "./EnterNewCardNumber";
import { useCardSwapSearchResultsHandlers } from "./useCardSwapSearchResultsHandlers";
import { CardSwapSearchResultsPagination } from "./CardSwapSearchResultsPagination";
import VspPortalContext from "../../../store/VspPortalContext";
import InfoIcon from '@mui/icons-material/Info';


const dataGridSx = {

    cursor: 'pointer',
    '.MuiDataGrid-columnHeaderTitle': { 
        fontWeight: 'bold !important',
        overflow: 'visible !important'
    },

    '.MuiDataGrid-cell:focus': {
        outline: 'none'
    }

};


type CardSwapSearchResultsProps = {

    cardSwapStatuses : string[ ];
    refresh : number;
    villageCodes : string[ ];

};


export const CardSwapSearchResults: React.FC< CardSwapSearchResultsProps > = ( props: CardSwapSearchResultsProps ) => {

    const { cardSwapStatuses, refresh, villageCodes } = props;

    const [ isPaginationInitialized, setIsPaginationInitialized ] = useState< boolean >( false );

    const { state } = useContext( VspPortalContext );

    const columns: GridColDef[ ] = [ 

        {
            field: 'firstName',
            headerName: 'First Name',
            width: 200,
        },
        
        {
            field: 'surname',
            headerName: 'Last Name',
            width: 200,
        },
    
        {
            field: 'email',
            headerName: 'Email',
            width: 250,
        },
    
        {
            field: 'employeeNumber',
            headerName: 'Employee / ERMS ID',
            width: 200,
        },
    
        {
            field: 'newCardNumber',
            headerName: 'New Card',
            width: 150,
            renderCell: params =>
                <>
                    { 
                        
                        params.row.id === inlineEditingRowId
                            ?
                        <EnterNewCardNumber 
                            cardSwapSearchResultId={ inlineEditingRowId }
                            onClose={ handleCancelInlineEditing } 
                        />
                            :
                        <>
                            {
                        
                            
                            params.row.newCardNumber
                                ?
                            <span>{ params.row.newCardNumber }</span>
                                :
                            <span style={{ color: 'gray'}}>Click to Add</span>
                            
                            }
                        </>
                    
                    }
                </>
        },
    
        {
            field: 'village',
            headerName: 'Card Collection Village',
            width: 250,
        },
    
        {
            field: 'cardSwapStatus',
            headerName: 'Status',
            width: 250,
        },
    
        {
            field: 'actions',
            headerName: 'Actions',
            width: 230,
            renderCell: params =>
                <>
                    <Tooltip title="Audit">
                        <IconButton onClick={ ( e ) => handleAuditButtonClick( e, params.row )}>
                            <InfoIcon />
                        </IconButton> 
                    </Tooltip>
                    &nbsp;
                    &nbsp;
                    &nbsp;
                    {
                         
                        params.row.cardSwapStatus === cardSwapStatusCodes[ CardSwapStatusCode.NewCardCreated ]
                            &&
                        <>
                            {
                                isSendingNotification
                                    ?
                                <CircularProgress size="22px" sx={{ marginLeft: '8px', color: '#e65400' }}/>
                                    :
                                <Button onClick={ e => handleNotifyForCollectionClick( e, params.row )} sx={{ ...bhpButtonSx, fontSize: '12px' }}>
                                    Notify For Collection
                                </Button>
                            }
                        </>
                    }
                </>
        },
    
    ];


    const { 
        auditDialogOpen
        , auditCardSwapId
        , cardCollectNotificationFailedDialogOpen
        , cardSwapFinalDialogOpen
        , cardSwapSearchDto
        , cardSwapToggleDialogOpen
        , creatingNewCardDialogOpen
        , handleAuditButtonClick
        , handleAuditDialogClose
        , handleCancelInlineEditing
        , handleCreatingNewCardDialogClose
        , handleCardCollectNotificationFailedDialogClose
        , handleCardSwapFinalDialogClose
        , handleCardSwapToggleDialogClose
        , handleCellClick
        , handleExportClick
        , handleFetchData
        , handleNewCardCreatedDialogClose
        , handleNotifyForCollectionClick
        , handleSortModelChange
        , inlineEditingRowId
        , isLoading
        , isSendingNotification
        , newCardCreatedDialogOpen
        , rows
        , selectedCardSwapSearchResult
        , sortModel
    } = useCardSwapSearchResultsHandlers( cardSwapStatuses, villageCodes );
    

    useEffect( () => {
        
        handleFetchData( );

    }, [ cardSwapStatuses, refresh, villageCodes ]);


    useEffect( () => {

        if( !isPaginationInitialized ){

            setIsPaginationInitialized( true );
            return;

        }

        const timer = setTimeout( ( ) =>  handleFetchData( ), 1000); 
     
        return () => { 
     
           clearTimeout( timer ); 
     
        };

    }, [ state.cardSwapSearchResultPageNumber, state.cardSwapSearchResultPageSize, state.cardSwapSearchResultSorting ]);


    return(
        <>
            {
                isLoading
                    ?
                <Box 
                    display="flex" 
                    alignItems="center"
                    justifyContent="center"
                    marginTop={ 50 }
                >
                    <CircularProgress size="80px" sx={{color: '#e65400'}}/>
                </Box>

                    :
   
                <Stack direction="column" display="flex" alignItems="center" gap={ 0.5 }>

                    {
                        rows.length > 0 && cardSwapSearchDto
                            ?
                        <>
                            <DataGrid
                                getRowId={ row => row.id }
                                onCellClick={ handleCellClick }
                                hideFooterPagination={ true }
                                rows={ rows }
                                columns={ columns }
                                autoHeight={ true }
                                sortModel={ sortModel }
                                onSortModelChange={ model => handleSortModelChange( model )  }
                                sx={{ ...dataGridSx, width: 1740, boxShadow: 5, marginTop: '20px !important' }}
                                sortingOrder={['desc', 'asc']}
                            />
                            <Box
                                alignItems="center"
                                display="flex" 
                                justifyContent="flex-end"
                                marginTop={ 1 }
                                width={ 1740 }
                            >
                                <CardSwapSearchResultsPagination totalCount={ cardSwapSearchDto.totalCount } />
                                <Button 
                                    sx={{ ...bhpButtonSx, width: '150px' }}
                                    onClick={ handleExportClick } 
                                    startIcon={ <GetAppIcon /> }
                                >
                                    Export
                                </Button>
                            </Box>

                    
                        </>
                            :
                        <span style={{ marginTop: '30px', fontSize: '20px' }}>No data to display</span>
                    }

                </Stack>

            }

            {
                auditCardSwapId
                    &&
                <AuditDialog 
                    cardSwapId = { auditCardSwapId }
                    dialogOpen={ auditDialogOpen }
                    onClose={ handleAuditDialogClose } 
                />
            }

            {
                selectedCardSwapSearchResult
                    &&
                <CardCollectNotificationFailedDialog 
                    cardSwapSearchResultDto = { selectedCardSwapSearchResult }
                    dialogOpen={ cardCollectNotificationFailedDialogOpen }
                    onClose={ handleCardCollectNotificationFailedDialogClose } 
                />
            }


            {
                selectedCardSwapSearchResult
                    &&
                <CreatingNewCardDialog 
                    cardSwapSearchResultDto = { selectedCardSwapSearchResult }
                    dialogOpen={ creatingNewCardDialogOpen }
                    onClose={ handleCreatingNewCardDialogClose } 
                />
            }

            {
                selectedCardSwapSearchResult
                    &&
                <NewCardCreatedDialog 
                    cardSwapSearchResultDto = { selectedCardSwapSearchResult }
                    dialogOpen={ newCardCreatedDialogOpen }
                    onClose={ handleNewCardCreatedDialogClose } 
                />
            }

            {
                selectedCardSwapSearchResult
                    &&
                <CardSwapToggleDialog 
                    cardSwapSearchResultDto = { selectedCardSwapSearchResult }
                    dialogOpen={ cardSwapToggleDialogOpen }
                    onClose={ handleCardSwapToggleDialogClose } 
                />
            }

            {
                selectedCardSwapSearchResult
                    &&
                <CardSwapFinalDialog 
                    cardSwapSearchResultDto = { selectedCardSwapSearchResult }
                    dialogOpen={ cardSwapFinalDialogOpen }
                    onClose={ handleCardSwapFinalDialogClose } 
                />
            }

        </>
    );

};